// The render sidecar's advice about what a board panel draws, as Inspect shows
// it (TOM-1044). The sidecar's state sweep (TOM-1032) reports a panel whose
// drawn depth or anchor state differs from the reading. It is advice, never a
// block: the panel stays as the owner made it, and Inspect says what the
// reading is. Read with the board (neoBoard.ts), so it lives apart from the
// rest of Inspect.

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** One piece of advice about what the panel draws, as Inspect shows it. */
export interface InspectAdvice {
  text: string;
  path: string | null;
}

/** Depth and anchor state: the readings a wrong drawing of could put the boat at risk. */
function isDepthOrAnchor(path: string | null, code: string): boolean {
  if (path !== null && (path.startsWith('environment.depth') || path.includes('anchor'))) {
    return true;
  }
  return /depth|anchor/i.test(code);
}

/**
 * The advice on an item that its drawn depth or anchor state differs from
 * the reading. The render sidecar's state sweep writes it (TOM-1032); until a
 * board read carries it, there is none. Read loosely, so a field this build
 * does not know never costs the sheet: an entry needs its text, and a code or
 * path naming depth or the anchor, with a code that says the drawing differs.
 */
export function inspectAdvice(item: unknown): InspectAdvice[] {
  if (!isRecord(item) || !Array.isArray(item.advice)) return [];
  const out: InspectAdvice[] = [];
  for (const raw of item.advice) {
    if (!isRecord(raw) || typeof raw.text !== 'string' || raw.text.trim() === '') continue;
    const code =
      typeof raw.code === 'string' ? raw.code : typeof raw.kind === 'string' ? raw.kind : '';
    const path = typeof raw.path === 'string' ? raw.path : null;
    if (!/differ|mismatch/i.test(code)) continue;
    if (!isDepthOrAnchor(path, code)) continue;
    out.push({ text: raw.text.trim(), path });
  }
  return out;
}
